import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCheck,
  ClipboardCheck,
  Clock,
  Download,
  FilePenLine,
  FileText,
  FileUp,
  FileX,
  Inbox,
  Link as LinkIcon,
  Loader2,
  Paperclip,
  RotateCw,
  ScanLine,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { apiClient } from '@shared/api';
import { ScanImage } from '../ScanImage';
import { documentCaptureService } from '../../services/documentCaptureService';
import { useDocumentCaptureStore } from '../../store/documentCaptureStore';
import {
  DocReviewReason,
  DocSupersedeState,
  provisionalVersion,
  versionDisplayState,
  type DocumentView,
  type FileDuplicateInfo,
  type FilePreflightResult,
  type ReviewQueueItem,
} from '../../types/documentCapture.types';
import { apiErrorInfo, formatBytes, formatVersionStamp, pluralPages } from '../../lib/versionFormat';
import { tabForItem } from '../../config/reviewTabs';

/**
 * The deliberate update lane (86d3uc89n T7 · async accept 86d3vbwup W9): take the file out → edit it
 * in Acrobat → put it back. Commit is an ACCEPT, not an answer — the server takes the file at once,
 * decodes in the background (a minute or two; up to ~15 minutes with retries) and the verdict
 * resolves here in place (or lands in Review if the user leaves). The match gate is stricter than
 * the old lane: a file whose barcode reads a DIFFERENT document — or none at all — is held for a
 * reviewer; only a verified match goes live on its own. The current version stays live throughout.
 * The step-3 card MORPHS on the preflight verdict so the button never promises what it can't deliver.
 */
interface UpdateFilePanelProps {
  doc: DocumentView;
  onClose: () => void;
  /** Fresh view after the accept — the caller swaps the document; the panel STAYS OPEN on the acknowledgment. */
  onCommitted: (doc: DocumentView) => void;
  /** Fresh view after a 409/poll refetch — the caller swaps the document, panel stays open. */
  onDocRefreshed: (doc: DocumentView) => void;
}

type UpdateStep =
  | 'start'
  | 'checking'
  | 'ready'
  /** Accepted — DOP is reading it (polling for the verdict). */
  | 'accepted'
  | 'promoted'
  | 'held'
  | 'failed'
  /** The provisional row was displaced/decided by someone else while we watched. */
  | 'resolved-elsewhere';

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const VERDICT_POLL_MS = 5_000;

const HOLD_VERDICTS = new Set(['mismatch', 'multiple', 'none']);

export function UpdateFilePanel({ doc, onClose, onCommitted, onDocRefreshed }: UpdateFilePanelProps) {
  const navigate = useNavigate();
  const openReview = useDocumentCaptureStore((s) => s.openReview);

  const [step, setStep] = useState<UpdateStep>('start');
  const [file, setFile] = useState<File | null>(null);
  const [preflight, setPreflight] = useState<FilePreflightResult | null>(null);
  const [note, setNote] = useState('');
  const [downloadedAt, setDownloadedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // W10 — the decode reserve was momentarily full (503): its own state, distinct from `error`,
  // because the file is fine and the fix is a retry, not a re-export.
  const [busy, setBusy] = useState(false);
  // A concurrent-change notice that must SURVIVE runPreflight's error reset (86d3uf18j S9) — kept
  // separate from `error` so the 409 re-preflight can't wipe it before it paints.
  const [staleNotice, setStaleNotice] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  /** The accepted provisional version's fileId — what the verdict poll watches. */
  const [acceptedFileId, setAcceptedFileId] = useState<number | null>(null);
  const [acceptedVersion, setAcceptedVersion] = useState<number | null>(null);
  /** The review item the verdict raised (held/failed) — drives the reason row + Open-review action. */
  const [verdictItem, setVerdictItem] = useState<ReviewQueueItem | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const downloadBtnRef = useRef<HTMLButtonElement>(null);
  const commitBtnRef = useRef<HTMLButtonElement>(null);
  const ackRef = useRef<HTMLDivElement>(null);
  // Request-generation guard (86d3uf18j S8): bumped on every preflight start and on clear/re-pick,
  // so a slow in-flight preflight that resolves after the file changed is dropped, not applied.
  const preflightGen = useRef(0);

  const activeVersionRow = doc.versions.find((v) => v.supersedeState === DocSupersedeState.Active);
  const pendingRow = provisionalVersion(doc.versions);
  const nextVersion = Math.max(0, ...doc.versions.map((v) => v.version ?? 0)) + 1;
  const duplicate = preflight?.duplicate ?? null;
  const pageDelta = preflight ? preflight.pageCount - doc.pageCount : 0;
  /** The W9 match gate, known BEFORE the click: hold verdicts submit for review, never promote. */
  const holds = preflight != null && HOLD_VERDICTS.has(preflight.verdict);

  const inAcknowledgment =
    step === 'accepted' || step === 'promoted' || step === 'held' || step === 'failed' || step === 'resolved-elsewhere';

  // Focus lands where the flow starts, then moves with it.
  useEffect(() => {
    downloadBtnRef.current?.focus();
  }, []);
  useEffect(() => {
    // Don't auto-focus commit while a concurrent-change notice is up (86d3uf18j S9).
    if (step === 'ready' && !staleNotice) commitBtnRef.current?.focus();
    if (inAcknowledgment) ackRef.current?.focus();
  }, [step, staleNotice, inAcknowledgment]);

  // Esc closes the PANEL (the viewer is a route page — there is no second Esc target). Safe at any
  // step: after the accept the decode continues server-side regardless.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !committing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, committing]);

  // ---- W9: the verdict poll. While accepted, refetch the document and read the provisional row's
  // derived state; resolves in place. (The live journey stream also mirrors it on the capture page.)
  useEffect(() => {
    if (step !== 'accepted' || acceptedFileId == null) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const view = await documentCaptureService.getDocument(doc.docNumber);
        if (cancelled) return;
        onDocRefreshed(view);
        const row = view.versions.find((v) => v.fileId === acceptedFileId);
        if (!row) {
          setStep('resolved-elsewhere');
          return;
        }
        const state = versionDisplayState(row);
        if (state === 'active') {
          setStep('promoted');
        } else if (state === 'held' || state === 'failed') {
          setVerdictItem(view.openReviews.find((r) => r.fileId === acceptedFileId) ?? null);
          setStep(state);
        } else if (state === 'history') {
          setStep('resolved-elsewhere');
        } else {
          // 86d403p4v M7 — 'checking' WITH AN OPEN REVIEW ITEM IS NOT STILL CHECKING.
          //
          // The row states only tell us the version has not settled; they cannot tell us whether
          // anything is still working on it. If a reviewer already holds an item for this exact file,
          // the answer has been handed to a person, and the card must stop saying "DOP is reading it —
          // usually takes a minute or two" and stop polling every 5s behind that sentence.
          //
          // M2 removed the route that made this common (a replacement no longer parks on a breaker or
          // quota hold; it goes to the gate, which flips the row to Decoded → 'held'). This stays as
          // the backstop for any path that writes the item without moving the row — the shape is
          // cheap to detect and the failure it prevents is the exact one this ticket exists for: a
          // file that looks busy forever while nothing is working on it.
          const openItem = view.openReviews.find((r) => r.fileId === acceptedFileId);
          if (openItem) {
            setVerdictItem(openItem);
            setStep('held');
          }
          // otherwise genuinely still checking → keep polling.
        }
      } catch {
        // Transient fetch miss — keep polling; the server keeps working either way.
      }
    }, VERDICT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [step, acceptedFileId, doc.docNumber, onDocRefreshed]);

  const downloadForEditing = useCallback(async () => {
    setDownloading(true);
    const v = doc.activeVersion;
    const name = `${doc.docNumber}${v != null ? `-v${v}` : ''}.pdf`;
    try {
      // A real download — the user is saving the file to edit it in Acrobat, so it is audited
      // (86d3uu104): this is exactly the "someone kept a copy" event worth recording.
      const res = await apiClient.get<Blob>(
        documentCaptureService.fileUrl(doc.docNumber, v ?? undefined, true),
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setDownloadedAt(
        new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
      );
      toast.info(name, { description: 'Edit it in Acrobat, then drop it back here.' });
    } catch {
      toast.error('Couldn’t download the current file.');
    } finally {
      setDownloading(false);
    }
  }, [doc.docNumber, doc.activeVersion]);

  const runPreflight = useCallback(
    async (candidate: File) => {
      const gen = ++preflightGen.current;
      setStep('checking');
      setPreflight(null);
      setError(null);
      setBusy(false);
      try {
        const result = await documentCaptureService.preflightFile(doc.docNumber, candidate);
        if (preflightGen.current !== gen) return; // cleared / re-picked while in flight — drop it
        setPreflight(result);
        setStep('ready');
      } catch (err) {
        if (preflightGen.current !== gen) return;
        const { status, message } = apiErrorInfo(err);
        if (status === 503) {
          // W10 busy refusal — arrives in ~1 s, file untouched, retry succeeds. Not an error.
          setBusy(true);
          setStep('start');
          return;
        }
        setError(
          message ?? 'Couldn’t read that file — it may be damaged. Re-export it from Acrobat and try again.'
        );
        setStep('start');
      }
    },
    [doc.docNumber]
  );

  const chooseFile = useCallback(
    (candidate: File) => {
      const isPdf =
        candidate.type === 'application/pdf' || candidate.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        setError('Only PDF files can be uploaded here.');
        return;
      }
      if (candidate.size > MAX_FILE_BYTES) {
        setError('That file is over the 25 MB limit.');
        return;
      }
      setFile(candidate);
      setNote('');
      setStaleNotice(null);
      void runPreflight(candidate);
    },
    [runPreflight]
  );

  const clearFile = useCallback(() => {
    preflightGen.current++; // invalidate any in-flight preflight so it can't paint onto the cleared panel
    setFile(null);
    setPreflight(null);
    setError(null);
    setBusy(false);
    setStaleNotice(null);
    setNote('');
    setStep('start');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const commit = useCallback(async () => {
    if (!file || !preflight || duplicate || committing) return;
    setCommitting(true);
    setError(null);
    try {
      // W9 — this is an ACCEPT: it returns within seconds with the fresh view carrying the new
      // provisional version. The decode + gate run server-side; the poll above watches them.
      const view = await documentCaptureService.updateFile(
        doc.docNumber,
        file,
        doc.linkEpoch,
        note.trim() || undefined
      );
      const provisional = provisionalVersion(view.versions);
      setAcceptedFileId(provisional?.fileId ?? null);
      setAcceptedVersion(provisional?.version ?? null);
      onCommitted(view);
      setStep('accepted');
    } catch (err) {
      const { status, message } = apiErrorInfo(err);
      if (status === 409) {
        // Someone else changed the document. Refresh it, re-run the checks against the fresh state,
        // and raise the notice in its OWN state (86d3uf18j S9) so runPreflight's setError(null)
        // can't wipe it before it paints.
        try {
          onDocRefreshed(await documentCaptureService.getDocument(doc.docNumber));
          void runPreflight(file);
          setStaleNotice('This document changed while you were working — it was refreshed. Check the versions, then try again.');
        } catch {
          setStaleNotice('This document changed while you were working — refresh the page and try again.');
        }
      } else if (status !== 403) {
        // 403 is toasted globally by the apiClient interceptor.
        setError(message ?? 'The upload didn’t go through. Please try again.');
      }
    } finally {
      setCommitting(false);
    }
  }, [file, preflight, duplicate, committing, doc.docNumber, doc.linkEpoch, note, onCommitted, onDocRefreshed, runPreflight]);

  const openReviewQueue = useCallback(() => {
    onClose();
    navigate('/documents');
    // Derive the tab (86d3w2jrj FE-1) — a held upload whose decode failed now lives in Scans.
    openReview(verdictItem ? tabForItem(verdictItem) : 'replacements', verdictItem?.id);
  }, [onClose, navigate, openReview, verdictItem]);

  return (
    <div
      className="grid grid-cols-1 rounded-xl border border-border bg-card lg:grid-cols-[1fr_372px]"
      // Dialog semantics for the in-place panel (86d3uf18j S11).
      role="dialog"
      aria-modal="true"
      aria-label={`Update file — ${doc.docNumber}`}
    >
      {/* ---- left: compare ---- */}
      <div className="flex flex-col border-b border-border bg-muted/30 lg:border-b-0 lg:border-r">
        <div className="flex items-center gap-2 border-b border-border bg-card p-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={onClose}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back to document
          </Button>
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Update file
          </span>
        </div>

        <div className="grid flex-1 content-start gap-3 p-4 sm:grid-cols-2">
          {/* current version */}
          <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-1.5">
              <span className="text-xs font-semibold text-foreground">
                Current · v{doc.activeVersion ?? '—'}
              </span>
              {activeVersionRow && (
                <span className="truncate text-xs tabular-nums text-muted-foreground">
                  {formatVersionStamp(activeVersionRow.uploadedAt)} · {pluralPages(doc.pageCount)}
                </span>
              )}
            </div>
            <div className="mt-1.5 h-60 overflow-hidden rounded-lg border border-border bg-card p-2">
              <ScanImage
                docNumber={doc.docNumber}
                page={1}
                // Version-pinned so the compare preview refreshes after a 409 onDocRefreshed (86d3uf18j M1).
                version={doc.activeVersion ?? undefined}
                alt={`${doc.docNumber} current version, page 1`}
                className="h-full w-full"
              />
            </div>
          </div>

          {/* new file */}
          <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-1.5">
              <span className="text-xs font-semibold text-foreground">New file</span>
              <span className="truncate text-xs tabular-nums text-muted-foreground">
                {file && preflight
                  ? `${formatBytes(preflight.sizeBytes)} · ${pluralPages(preflight.pageCount)}`
                  : file
                    ? formatBytes(file.size)
                    : 'not uploaded yet'}
              </span>
            </div>
            {step === 'checking' ? (
              <Skeleton className="mt-1.5 h-60 w-full rounded-lg" />
            ) : preflight ? (
              <div className="relative mt-1.5 h-60 overflow-hidden rounded-lg border-2 border-primary bg-card p-2">
                {preflight.previewPage1Base64 ? (
                  <img
                    src={`data:image/webp;base64,${preflight.previewPage1Base64}`}
                    alt="New file, page 1"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    <FileText className="h-6 w-6" aria-hidden="true" />
                    <span className="sr-only">Preview unavailable</span>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground">
                  <Paperclip className="h-3 w-3" aria-hidden="true" />
                  {pageDelta > 0
                    ? `+${pageDelta} ${pageDelta === 1 ? 'page' : 'pages'} appended`
                    : pageDelta < 0
                      ? `${pageDelta} pages`
                      : 'Same page count'}
                </div>
              </div>
            ) : (
              <div className="mt-1.5 flex h-60 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-center">
                <FilePenLine className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <p className="max-w-44 text-xs leading-relaxed text-muted-foreground">
                  Your edited copy shows up here once you upload it.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- right: steps OR the post-accept acknowledgment ---- */}
      <div className="flex flex-col gap-3 p-4">
        {inAcknowledgment ? (
          <AcknowledgmentCard
            ref={ackRef}
            step={step}
            doc={doc}
            acceptedVersion={acceptedVersion}
            verdictItem={verdictItem}
            onClose={onClose}
            onOpenReview={openReviewQueue}
          />
        ) : (
          <>
            {/* Already-pending guard — one pending version per document; a new upload displaces it. */}
            {pendingRow && (
              <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2.5">
                <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-foreground">
                  v{pendingRow.version} is already {versionDisplayState(pendingRow) === 'checking' ? 'being read' : 'waiting for a reviewer'}.
                  Uploading again replaces it — only one pending version per document.
                </p>
              </div>
            )}

            {/* step 1 — take the file out */}
            <StepCard number={1} title="Take the file out">
              <p className="text-xs leading-relaxed text-muted-foreground">
                Edit it in Acrobat — append attachments, add a signed page.{' '}
                <b className="font-semibold text-foreground">Keep the barcode page first</b> so DOP can
                still read it.
              </p>
              {downloadedAt ? (
                <div className="flex items-center gap-1.5 text-xs">
                  <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
                  <span className="flex-1 text-foreground">Downloaded {downloadedAt}</span>
                  <button
                    type="button"
                    onClick={() => void downloadForEditing()}
                    className="text-muted-foreground underline hover:text-foreground"
                  >
                    Again
                  </button>
                </div>
              ) : (
                <Button
                  ref={downloadBtnRef}
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={() => void downloadForEditing()}
                  disabled={downloading}
                >
                  {downloading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  ) : (
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Download for editing
                </Button>
              )}
            </StepCard>

            {/* step 2 — put it back */}
            <StepCard number={2} title="Put the edited file back">
              {file ? (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-info/12 text-info" aria-hidden="true">
                    <FileText className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-foreground">{file.name}</p>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {formatBytes(file.size)}
                      {preflight ? ` · ${pluralPages(preflight.pageCount)}` : ''} · just now
                    </p>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={clearFile}
                        disabled={committing || step === 'checking'}
                        aria-label="Choose a different file"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Choose a different file</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const dropped = e.dataTransfer.files?.[0];
                    if (dropped) chooseFile(dropped);
                  }}
                  className={cn(
                    'flex w-full flex-col items-center gap-1.5 rounded-lg border border-dashed border-input bg-muted/40 px-3 py-4 transition-colors',
                    dragActive ? 'border-primary bg-primary/5' : 'hover:border-primary hover:bg-primary/5'
                  )}
                >
                  <Upload className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="whitespace-nowrap text-xs font-semibold text-foreground">
                    Drop the edited PDF here
                  </span>
                  <span className="text-xs text-muted-foreground">or click to choose · PDF, up to 25 MB</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const picked = e.target.files?.[0];
                  if (picked) chooseFile(picked);
                }}
              />
            </StepCard>

            {/* checking state */}
            {step === 'checking' && (
              <div
                className="flex items-center gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2.5 text-xs text-info"
                aria-live="polite"
              >
                <ScanLine className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                Reading the barcode…
              </div>
            )}

            {/* W10 — busy refusal: instant, file untouched, retry re-runs the read */}
            {busy && file && (
              <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2.5" role="status">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-info">The scanner queue is busy right now</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    Nothing was lost — your file is still attached. Try again in a moment.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-1.5 h-7 gap-1.5"
                    onClick={() => void runPreflight(file)}
                  >
                    <RotateCw className="h-3 w-3" aria-hidden="true" />
                    Try again
                  </Button>
                </div>
              </div>
            )}

            {/* concurrent-change notice (86d3uf18j S9) */}
            {staleNotice && (
              <div className="flex items-start gap-2 rounded-lg border border-warning/35 bg-warning/5 px-3 py-2.5" role="alert">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-foreground">{staleNotice}</p>
              </div>
            )}

            {/* preflight / commit failure */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/35 bg-destructive/5 px-3 py-2.5" role="alert">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs leading-relaxed text-foreground">{error}</p>
                  {file && !preflight && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1.5 h-7"
                      onClick={() => void runPreflight(file)}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* duplicate — the one hard stop */}
            {duplicate && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/35 bg-destructive/5 px-3 py-2.5" role="alert">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                <p className="text-xs leading-relaxed text-foreground">{duplicateMessage(duplicate)}</p>
              </div>
            )}

            {/* warning banner — informs; hold verdicts route to review, they never block submission */}
            {preflight && !duplicate && preflight.verdict !== 'match' && (
              <WarnBanner preflight={preflight} doc={doc} />
            )}

            {/* step 3 — morphs on the verdict (W9): the button never promises what it can't deliver */}
            {step === 'ready' && preflight && (
              <StepCard number={3} title={holds ? 'Send it for review' : 'Confirm the swap'}>
                <ul className="space-y-1.5">
                  <CheckRow tone={preflight.verdict === 'match' ? 'success' : 'warning'}>
                    {barcodeCheckText(preflight, doc.docNumber)}
                  </CheckRow>
                  <CheckRow
                    tone={pageDelta > 0 ? 'info' : pageDelta < 0 ? 'warning' : 'success'}
                    icon={pageDelta > 0 ? 'paperclip' : pageDelta < 0 ? 'warning' : 'check'}
                  >
                    {pageDelta > 0
                      ? `${pluralPages(doc.pageCount)} → ${preflight.pageCount} pages (+${pageDelta} appended)`
                      : pageDelta < 0
                        ? `${doc.pageCount} pages → ${pluralPages(preflight.pageCount)} — fewer pages than the current file`
                        : `Same page count (${pluralPages(preflight.pageCount)})`}
                  </CheckRow>
                  {holds ? (
                    <CheckRow tone="info" icon="inbox">
                      Goes to review as v{nextVersion} · v{doc.activeVersion ?? '—'} stays live until approved
                    </CheckRow>
                  ) : (
                    <CheckRow tone="muted" icon="link">
                      Becomes v{nextVersion} · the NetSuite link stays the same
                    </CheckRow>
                  )}
                </ul>

                <div>
                  <label htmlFor="update-note" className="text-xs font-semibold text-muted-foreground">
                    Note <span className="font-normal">(optional)</span>
                  </label>
                  <Input
                    id="update-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Appended signed delivery copy"
                    className="mt-1 h-8 text-xs"
                    disabled={committing}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Button
                    ref={commitBtnRef}
                    size="sm"
                    className="w-full gap-1.5"
                    onClick={() => void commit()}
                    disabled={committing || duplicate != null}
                  >
                    {committing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    ) : holds ? (
                      <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    {holds ? 'Submit for review' : 'Make this the current version'}
                  </Button>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {holds
                      ? `v${doc.activeVersion ?? '—'} stays live. A reviewer either approves this as v${nextVersion} or keeps the current file.`
                      : 'DOP reads the file first — it goes live a minute or two after you press this.'}
                  </p>
                  <Button variant="outline" size="sm" className="w-full" onClick={onClose} disabled={committing}>
                    Cancel
                  </Button>
                </div>
              </StepCard>
            )}

            <p className="mt-auto pt-1 text-xs leading-relaxed text-muted-foreground">
              {holds
                ? `DOP can't match this file to ${doc.docNumber}, so a reviewer approves it before it replaces the current version. Nothing you do here changes what people see today.`
                : 'No approval needed for a file DOP can match — it goes live once the read finishes. The old version is kept, and the update is stamped with your name in the activity log.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- pieces

function StepCard({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <div className="flex items-center gap-2">
        <span
          className="flex h-5 w-5 items-center justify-center rounded-md bg-muted text-[10px] font-bold text-muted-foreground"
          aria-hidden="true"
        >
          {number}
        </span>
        <h4 className="text-xs font-semibold text-foreground">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function CheckRow({
  tone,
  icon,
  children,
}: {
  tone: 'success' | 'warning' | 'info' | 'muted';
  icon?: 'check' | 'warning' | 'paperclip' | 'link' | 'inbox';
  children: React.ReactNode;
}) {
  const resolved = icon ?? (tone === 'success' ? 'check' : 'warning');
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'info'
          ? 'text-info'
          : 'text-muted-foreground';
  return (
    <li className="flex items-start gap-1.5 text-xs leading-snug text-foreground">
      <span className={cn('mt-px shrink-0', toneClass)} aria-hidden="true">
        {resolved === 'check' && <Check className="h-3.5 w-3.5" />}
        {resolved === 'warning' && <AlertTriangle className="h-3.5 w-3.5" />}
        {resolved === 'paperclip' && <Paperclip className="h-3.5 w-3.5" />}
        {resolved === 'link' && <LinkIcon className="h-3.5 w-3.5" />}
        {resolved === 'inbox' && <Inbox className="h-3.5 w-3.5" />}
      </span>
      <span className="tabular-nums">{children}</span>
    </li>
  );
}

/**
 * W9 acknowledgment card — replaces steps 1–3 (and the old success toast) after the accept.
 * Resolves IN PLACE while the user stays; if they close, the version chips + review queue carry it.
 */
const AcknowledgmentCard = ({
  ref,
  step,
  doc,
  acceptedVersion,
  verdictItem,
  onClose,
  onOpenReview,
}: {
  ref: React.Ref<HTMLDivElement>;
  step: UpdateStep;
  doc: DocumentView;
  acceptedVersion: number | null;
  verdictItem: ReviewQueueItem | null;
  onClose: () => void;
  onOpenReview: () => void;
}) => {
  const v = acceptedVersion != null ? `v${acceptedVersion}` : 'the new version';

  /**
   * 86d403p4v D3/D4 — did the FILE fail, or did the CHECK fail? Same signal as the review panel and
   * the viewer chip: a row with pages was read successfully and its page images were written before
   * any verdict was taken, so the only thing that can have failed is the independent check. A row
   * with no pages never got that far. Reliable since M6 stopped the verify handler's park writing 0.
   */
  const acceptedRow = doc.versions.find((r) => r.version === acceptedVersion);
  const failedCheckOnly = step === 'failed' && (acceptedRow?.pageCount ?? 0) > 0;

  // 86d403p4v D3 — SupersedePending had no case here, so every hold this ticket introduces (the
  // second reader off, unavailable, out of budget, or a rescue that must not promote itself) fell
  // through to "No barcode to verify against". That is the one thing those holds are NOT: the barcode
  // read perfectly, which is precisely why the file got as far as the second reader. The fallback is
  // now neutral, and the exact reason is the server's own sentence, rendered verbatim in the body.
  const heldShortReason =
    verdictItem?.reason === DocReviewReason.HeldMismatch
      ? 'Its barcode is for another document'
      : verdictItem?.reason === DocReviewReason.HeldMultiple
        ? 'More than one number in the file'
        : verdictItem?.reason === DocReviewReason.HeldNoBarcode
          ? 'No barcode to verify against'
          : 'A reviewer needs to confirm it';

  const spec =
    step === 'accepted'
      ? {
          tone: 'info' as const,
          icon: <ScanLine className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />,
          title: 'Accepted — DOP is reading it',
          body: `Saved as ${v}. This usually takes a minute or two — you can close this and keep working.`,
          rows: [
            { tone: 'success' as const, icon: <Check className="h-3.5 w-3.5" />, text: `Accepted and saved as ${v} — nothing overwritten` },
            { tone: 'info' as const, icon: <ScanLine className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />, text: 'Reading the file…' },
            { tone: 'muted' as const, icon: <ShieldCheck className="h-3.5 w-3.5" />, text: `v${doc.activeVersion ?? '—'} stays live the whole time` },
          ],
        }
      : step === 'promoted'
        ? {
            tone: 'success' as const,
            icon: <CheckCheck className="h-4 w-4" aria-hidden="true" />,
            title: `${v} is live`,
            body: 'It replaced the previous version a moment ago.',
            rows: [
              { tone: 'success' as const, icon: <Check className="h-3.5 w-3.5" />, text: `Barcode matched ${doc.docNumber}` },
              { tone: 'success' as const, icon: <FileUp className="h-3.5 w-3.5" />, text: `${v} is now the current version` },
              { tone: 'muted' as const, icon: <LinkIcon className="h-3.5 w-3.5" />, text: 'Same NetSuite link — nothing to update' },
            ],
          }
        : step === 'held'
          ? {
              tone: 'warning' as const,
              icon: <Inbox className="h-4 w-4" aria-hidden="true" />,
              title: 'Sent to review',
              body:
                verdictItem?.reason === DocReviewReason.HeldMismatch
                  ? `The barcode reader got ${verdictItem.reasonValue ?? 'another document'} from it, so a reviewer decides whether it belongs on ${doc.docNumber}.`
                  : verdictItem?.reason === DocReviewReason.HeldMultiple
                    ? 'It reads more than one document number, so a reviewer confirms which one it belongs to.'
                    : verdictItem?.reason === DocReviewReason.HeldNoBarcode
                      ? `There's no barcode to verify it against ${doc.docNumber}, so a reviewer checks it first.`
                      : // 86d403p4v D3 — the server's sentence, verbatim. Every hold added by this
                        // ticket explains a DIFFERENT thing (reader off / down / out of budget / a
                        // background rescue that must not promote unattended), and there is no way to
                        // re-derive which from the reason enum, because they deliberately share
                        // SupersedePending. Guessing here is what produced the barcode lie above.
                        (verdictItem?.reasonDetail ??
                        `A reviewer checks this one before it replaces the current version of ${doc.docNumber}.`),
              rows: [
                { tone: 'success' as const, icon: <Check className="h-3.5 w-3.5" />, text: `Accepted and saved as ${v}` },
                { tone: 'warning' as const, icon: <AlertTriangle className="h-3.5 w-3.5" />, text: heldShortReason },
                { tone: 'muted' as const, icon: <ShieldCheck className="h-3.5 w-3.5" />, text: `v${doc.activeVersion ?? '—'} stays live until a reviewer approves` },
              ],
            }
          : step === 'failed'
            ? {
                tone: 'destructive' as const,
                icon: <FileX className="h-4 w-4" aria-hidden="true" />,
                // 86d403p4v D3 — the headline and body both used to assert the file was broken. This
                // step is also reached when the file read perfectly and the INDEPENDENT CHECK gave up
                // (the A3 dead-letter park), and telling that user their PDF is corrupt is what sent
                // people back to re-export good files. `failedCheckOnly` reads the page count, which is
                // real on those rows since M6 and 0 on a genuinely unopenable file.
                title: failedCheckOnly ? 'That upload wasn’t confirmed' : 'That file couldn’t be read',
                body: verdictItem?.reasonDetail ??
                  (failedCheckOnly
                    ? 'The file was read fine, but the independent check couldn’t be completed, so nothing confirmed it.'
                    : 'DOP tried several times and couldn’t read it. That may be the file, or it may have been a problem on our side.'),
                rows: [
                  { tone: 'success' as const, icon: <Check className="h-3.5 w-3.5" />, text: `Accepted and saved as ${v}` },
                  { tone: 'destructive' as const, icon: <FileX className="h-3.5 w-3.5" />, text: failedCheckOnly ? 'The independent check couldn’t be completed' : 'Couldn’t be read after several attempts' },
                  { tone: 'success' as const, icon: <ShieldCheck className="h-3.5 w-3.5" />, text: `v${doc.activeVersion ?? '—'} is untouched and still live` },
                ],
              }
            : {
                tone: 'info' as const,
                icon: <Inbox className="h-4 w-4" aria-hidden="true" />,
                title: 'Someone else resolved it',
                body: 'This upload was replaced or decided while you were watching — check the versions.',
                rows: [],
              };

  const toneBorder =
    spec.tone === 'success'
      ? 'border-success/30 bg-success/5'
      : spec.tone === 'warning'
        ? 'border-warning/30 bg-warning/5'
        : spec.tone === 'destructive'
          ? 'border-destructive/30 bg-destructive/5'
          : 'border-info/30 bg-info/5';
  const toneTile =
    spec.tone === 'success'
      ? 'bg-success/15 text-success'
      : spec.tone === 'warning'
        ? 'bg-warning/15 text-warning'
        : spec.tone === 'destructive'
          ? 'bg-destructive/15 text-destructive'
          : 'bg-info/15 text-info';

  const needsReview = step === 'held' || step === 'failed';

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className={cn('flex flex-col gap-3 rounded-lg border p-3.5 outline-none', toneBorder)}
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', toneTile)} aria-hidden="true">
          {spec.icon}
        </span>
        <h4 className="text-sm font-semibold text-foreground">{spec.title}</h4>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{spec.body}</p>

      {spec.rows.length > 0 && (
        <ul className="space-y-1.5">
          {spec.rows.map((row, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs leading-snug text-foreground">
              <span
                className={cn(
                  'mt-px shrink-0',
                  row.tone === 'success'
                    ? 'text-success'
                    : row.tone === 'warning'
                      ? 'text-warning'
                      : row.tone === 'destructive'
                        ? 'text-destructive'
                        : row.tone === 'info'
                          ? 'text-info'
                          : 'text-muted-foreground'
                )}
                aria-hidden="true"
              >
                {row.icon}
              </span>
              <span>{row.text}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-1.5 pt-1">
        {needsReview && (
          <Button size="sm" className="w-full gap-1.5" onClick={onOpenReview}>
            <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Open the review queue
          </Button>
        )}
        <Button
          variant={needsReview ? 'outline' : step === 'accepted' ? 'outline' : 'default'}
          size="sm"
          className="w-full"
          onClick={onClose}
        >
          {step === 'accepted' ? 'Close — it keeps going' : 'Back to document'}
        </Button>
      </div>
    </div>
  );
};

function WarnBanner({ preflight, doc }: { preflight: FilePreflightResult; doc: DocumentView }) {
  let title: string;
  let body: string;
  switch (preflight.verdict) {
    case 'moved':
      title = `The barcode is now on page ${preflight.barcodePage}, not page 1`;
      body = `Appending the attachments pushed the Capitol page down. DOP still matched ${doc.docNumber}, so this one still goes live on its own — reordering it first keeps future re-scans clean.`;
      break;
    case 'mismatch':
      title = 'This file belongs to a different document';
      body = `The barcode reader got ${preflight.barcodeNumber} from it. It won't replace ${doc.docNumber} on your word alone — submit it and a reviewer decides, or cancel and open ${preflight.barcodeNumber} instead.`;
      break;
    case 'multiple':
      title = 'This file reads more than one document number';
      body = `${preflight.numbers.join(' + ')} — one document per file. A reviewer confirms which one it belongs to before it replaces the current version.`;
      break;
    default:
      title = 'No Capitol barcode found in this file';
      body = `DOP can't verify this file matches ${doc.docNumber}, so a reviewer checks it before it replaces the current version.`;
  }
  return (
    <div className="flex items-start gap-2 rounded-lg border border-warning/35 bg-warning/5 px-3 py-2.5">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-warning">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function barcodeCheckText(preflight: FilePreflightResult, docNumber: string): string {
  switch (preflight.verdict) {
    case 'match':
      return `Barcode matches ${docNumber} on page 1`;
    case 'moved':
      return `Barcode matches ${docNumber}, but it sits on page ${preflight.barcodePage} now`;
    case 'mismatch':
      return `The barcode reader got ${preflight.barcodeNumber} — not the document you're on`;
    case 'multiple':
      return `Reads ${preflight.numbers.join(' + ')} — more than one document number`;
    default:
      return 'No Capitol barcode found on any page';
  }
}

/** Client-side mirror of the server's duplicate refusals — shown pre-commit so the stop is instant. */
function duplicateMessage(duplicate: FileDuplicateInfo): string {
  switch (duplicate.scope) {
    case 'current':
      return "That's exactly the file that's already current — nothing to update.";
    case 'olderVersion':
      return `This file is identical to v${duplicate.version} — use Restore on that version instead.`;
    case 'pendingReview':
      return `This file matches v${duplicate.version}, which is awaiting a replacement decision — resolve it in Review instead.`;
    case 'unprocessedScan': {
      const who = [
        duplicate.uploadedAt ? `scanned in ${formatVersionStamp(duplicate.uploadedAt)}` : null,
        duplicate.pageCount != null ? pluralPages(duplicate.pageCount) : null,
      ]
        .filter(Boolean)
        .join(', ');
      return `This exact file is already in DOP${who ? ` (${who})` : ''} — it was never attached to a document and is still waiting in the review queue. Open Review → Scans to assign or reject it.`;
    }
    default:
      return duplicate.docNumber
        ? `This exact file is already in DOP as ${duplicate.docNumber}.`
        : 'This exact file already exists in DOP as a document outside your access.';
  }
}
